// Admin Review → Announcements (2026-10-27). Write a status update or a system alert,
// pick who sees it and for how long, and it appears as the yellow-orange bar on every
// page for that audience until each person acknowledges it or the time runs out.
// Below: everything posted, who has acknowledged, and End now. Backend: src/announcements.js.
import { useState, useEffect, useCallback } from "react";
import API from "../api";
import { Megaphone, AlertTriangle, Clock, RefreshCw, Send, X, Users, Building2, CheckCircle2, Hourglass } from "lucide-react";
import { ConvoStyles } from "./conversation/Conversation";
import { AnnouncementCard } from "./AnnouncementBar";

const KINDS = {
  status: { label: "Status update", Icon: Megaphone,     hint: "Something people should know: planned work, a change, a heads-up." },
  alert:  { label: "System alert",  Icon: AlertTriangle, hint: "Something is wrong or about to be: an outage, a security issue." },
};
const AUDIENCES = {
  staff:  { label: "MIDTC staff",              Icon: Users },
  portal: { label: "Client portal",            Icon: Building2 },
  all:    { label: "Staff and client portal",  Icon: Users },
};
const DURATIONS = [
  { k: "30",    label: "30 min",  m: 30 },
  { k: "60",    label: "1 hour",  m: 60 },
  { k: "120",   label: "2 hours", m: 120 },
  { k: "240",   label: "4 hours", m: 240 },
  { k: "480",   label: "8 hours", m: 480 },
  { k: "1440",  label: "1 day",   m: 1440 },
  { k: "4320",  label: "3 days",  m: 4320 },
  { k: "10080", label: "1 week",  m: 10080 },
  { k: "custom", label: "Until…" },
];
const MAX = 1000;

const fmtET = (d) => (d ? new Date(d).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET" : "—");
function left(d) {
  const m = Math.round((new Date(d).getTime() - Date.now()) / 60000);
  if (m < 1) return "less than a minute";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  if (h < 24) return r ? `${h}h ${r}m` : `${h}h`;
  const days = Math.floor(h / 24), rh = h % 24;
  return rh ? `${days}d ${rh}h` : `${days}d`;
}
// datetime-local wants "YYYY-MM-DDTHH:MM" in the browser's own time zone.
const localInput = (d) => { const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return x.toISOString().slice(0, 16); };

const pill = (c) => ({ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 999,
  color: c, background: `color-mix(in srgb, ${c} 14%, transparent)`, border: `1px solid color-mix(in srgb, ${c} 40%, transparent)`, whiteSpace: "nowrap" });
const panelStyle = { background: "var(--t-gloss, none), var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: "var(--t-radius-lg, 16px)", boxShadow: "var(--t-shadow-panel)" };
const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "var(--t-text-label)", margin: "0 0 6px" };

function Compose({ onPosted }) {
  const [kind, setKind] = useState("status");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState("staff");
  const [dur, setDur] = useState("60");
  const [until, setUntil] = useState(() => localInput(new Date(Date.now() + 2 * 3600000)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const custom = dur === "custom";
  const endsAt = custom ? (until ? new Date(until) : null) : new Date(Date.now() + DURATIONS.find((d) => d.k === dur).m * 60000);
  const endsOk = endsAt && !isNaN(endsAt) && endsAt.getTime() > Date.now() + 60000;

  const post = async () => {
    setBusy(true); setError(null);
    try {
      const body = { kind, audience, message: message.trim(), ...(custom ? { ends_at: endsAt.toISOString() } : { duration_minutes: Number(dur) }) };
      const r = await API.post("/admin/announcements", body);
      setMessage("");
      onPosted(r.data);
    } catch (e) { setError(e.response?.data?.error || "Couldn't post the message. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ ...panelStyle, padding: 20, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Megaphone size={16} style={{ color: "#f59e0b" }} />
        <h2 style={{ fontSize: 15, margin: 0 }}>Post an announcement</h2>
      </div>
      <p style={{ fontSize: 12.5, color: "var(--t-text-faint)", margin: "0 0 16px" }}>
        Shows as a yellow-orange bar at the top of every page (on the Dashboard, right above Today's boost) until each person presses Acknowledge or the time runs out.
      </p>

      <span style={lbl}>Kind</span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        {Object.entries(KINDS).map(([k, K]) => (
          <button key={k} type="button" className={`cv-chip${kind === k ? " on" : ""}`} aria-pressed={kind === k} onClick={() => setKind(k)}>
            <K.Icon size={14} /> {K.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--t-text-faint)", marginBottom: 14 }}>{KINDS[kind].hint}</div>

      <label style={{ display: "block", marginBottom: 14 }}>
        <span style={lbl}>Message</span>
        <textarea className="cv-input" rows={3} value={message} maxLength={MAX} onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Ticketing will be down for maintenance tonight from 10 PM to 11 PM ET."
          style={{ width: "100%", padding: "10px 12px", fontSize: 13.5, lineHeight: 1.55, resize: "vertical", borderRadius: "var(--t-radius-sm, 8px)" }} />
        <span style={{ display: "block", fontSize: 11.5, color: "var(--t-text-faint)", marginTop: 4, textAlign: "right" }}>{message.length}/{MAX}</span>
      </label>

      <span style={lbl}>Who sees it</span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {Object.entries(AUDIENCES).map(([k, A]) => (
          <button key={k} type="button" className={`cv-chip${audience === k ? " on" : ""}`} aria-pressed={audience === k} onClick={() => setAudience(k)}>
            <A.Icon size={14} /> {A.label}
          </button>
        ))}
      </div>

      <span style={lbl}>How long it shows</span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        {DURATIONS.map((d) => (
          <button key={d.k} type="button" className={`cv-chip${dur === d.k ? " on" : ""}`} aria-pressed={dur === d.k} onClick={() => setDur(d.k)}
            style={{ height: 30, fontSize: 12.5 }}>{d.label}</button>
        ))}
      </div>
      {custom && (
        <label style={{ display: "block", marginBottom: 8, maxWidth: 280 }}>
          <span style={{ ...lbl, fontWeight: 600 }}>Show until (your time)</span>
          <input type="datetime-local" className="cv-input" value={until} min={localInput(new Date())} onChange={(e) => setUntil(e.target.value)}
            style={{ width: "100%", height: 38, padding: "0 12px", fontSize: 13.5, borderRadius: "var(--t-radius-sm, 8px)" }} />
        </label>
      )}
      <div style={{ fontSize: 12, color: endsOk ? "var(--t-text-muted)" : "var(--badge-red)", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
        <Clock size={13} /> {endsOk ? `Ends ${fmtET(endsAt)}` : "Pick an end time at least a minute from now."}
      </div>

      <span style={lbl}>Preview</span>
      <div style={{ marginBottom: 16, pointerEvents: "none" }}>
        <AnnouncementCard kind={kind} message={message.trim() || "Your message will appear here."} />
      </div>

      {error && <div style={{ fontSize: 12.5, color: "var(--badge-red)", marginBottom: 10 }}>{error}</div>}
      <button type="button" className="cv-btn cv-btn-primary" disabled={busy || !message.trim() || !endsOk} onClick={post}>
        <Send size={14} /> {busy ? "Posting…" : "Post announcement"}
      </button>
    </div>
  );
}

function Acks({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    API.get(`/admin/announcements/${id}/acks`).then((r) => setD(r.data)).catch(() => setD({ acknowledged: [], waiting: [], failed: true }));
  }, [id]);
  if (!d) return <div style={{ padding: "8px 0", fontSize: 12.5, color: "var(--t-text-faint)" }}>Loading…</div>;
  if (d.failed) return <div style={{ padding: "8px 0", fontSize: 12.5, color: "var(--badge-red)" }}>Couldn't load who acknowledged.</div>;
  const col = { flex: "1 1 260px", minWidth: 0 };
  const row = { display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--t-border-softer)" };
  return (
    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", paddingTop: 8 }}>
      <div style={col}>
        <div style={{ ...lbl, display: "flex", alignItems: "center", gap: 6 }}><CheckCircle2 size={13} color="#22c55e" /> Acknowledged ({d.acknowledged.length})</div>
        {d.acknowledged.length === 0 ? <div style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>No one yet.</div>
          : d.acknowledged.map((p, i) => (
            <div key={i} style={row}>
              <span style={{ color: "var(--t-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.name}{!p.staff && p.client_name ? <span style={{ color: "var(--t-text-faint)" }}> · {p.client_name}</span> : null}
              </span>
              <span style={{ color: "var(--t-text-faint)", whiteSpace: "nowrap" }}>{fmtET(p.acked_at)}</span>
            </div>
          ))}
      </div>
      {d.waiting.length > 0 && (
        <div style={col}>
          <div style={{ ...lbl, display: "flex", alignItems: "center", gap: 6 }}><Hourglass size={13} color="#f59e0b" /> Staff who haven't yet ({d.waiting.length})</div>
          {d.waiting.map((p) => (
            <div key={p.email} style={row}><span style={{ color: "var(--t-text-body)" }}>{p.name}</span><span style={{ color: "var(--t-text-faint)" }}>{p.email}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function List({ data, onReload }) {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const end = async (a) => {
    if (!window.confirm("End this announcement now? It disappears from everyone's screen.")) return;
    setBusy(a.id);
    try { await API.post(`/admin/announcements/${a.id}/end`); } catch (e) { window.alert(e.response?.data?.error || "Couldn't end it."); }
    finally { setBusy(null); onReload(); }
  };
  const rows = data?.announcements;
  return (
    <div style={{ ...panelStyle, padding: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 20px", borderBottom: "1px solid var(--t-border-softer)" }}>
        <Clock size={16} style={{ color: "var(--t-text-faint)" }} />
        <h2 style={{ fontSize: 15, margin: 0 }}>Posted announcements</h2>
        <div style={{ flex: 1 }} />
        <button type="button" className="cv-btn" onClick={onReload}><RefreshCw size={14} /> Refresh</button>
      </div>
      {!rows ? <div style={{ padding: 30, textAlign: "center", color: "var(--t-text-faint)" }}>Loading…</div>
        : rows.length === 0 ? <div style={{ padding: 36, textAlign: "center", color: "var(--t-text-faint)", fontSize: 13.5 }}>Nothing posted yet.</div>
        : rows.map((a) => {
          const K = KINDS[a.kind] || KINDS.status;
          const state = a.live ? { t: `Live · ${left(a.ends_at)} left`, c: "#f59e0b" }
            : a.ended_at ? { t: `Ended early by ${a.ended_by_name || "an admin"}`, c: "#64748b" }
            : a.upcoming ? { t: "Scheduled", c: "#2563eb" } : { t: "Expired", c: "#64748b" };
          const acks = [
            a.audience !== "portal" ? `${a.staff_acks} of ${data.staff_total} staff` : null,
            a.audience !== "staff" ? `${a.portal_acks} client${a.portal_acks === 1 ? "" : "s"}` : null,
          ].filter(Boolean).join(" · ");
          return (
            <div key={a.id} style={{ padding: "14px 20px", borderBottom: "1px solid var(--t-border-softer)", opacity: a.live ? 1 : 0.8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                <span style={pill(a.kind === "alert" ? "#f97316" : "#f59e0b")}><K.Icon size={11} /> {K.label}</span>
                <span style={pill("#64748b")}>{(AUDIENCES[a.audience] || AUDIENCES.staff).label}</span>
                <span style={pill(state.c)}>{state.t}</span>
                <div style={{ flex: 1 }} />
                <button type="button" className="cv-link" onClick={() => setOpen(open === a.id ? null : a.id)}>
                  {open === a.id ? "Hide" : `Acknowledged: ${acks}`}
                </button>
                {a.live && <button type="button" className="cv-btn cv-btn-danger" disabled={busy === a.id} onClick={() => end(a)}><X size={14} /> End now</button>}
              </div>
              <div style={{ fontSize: 13.5, color: "var(--t-text-primary)", whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.5 }}>{a.message}</div>
              <div style={{ fontSize: 11.5, color: "var(--t-text-faint)", marginTop: 5 }}>
                Posted {fmtET(a.created_at)} by {a.created_by_name || "—"} · {a.ended_at ? `ended ${fmtET(a.ended_at)}` : `${a.live ? "ends" : "ended"} ${fmtET(a.ends_at)}`}
              </div>
              {open === a.id && <Acks id={a.id} />}
            </div>
          );
        })}
    </div>
  );
}

export default function AnnouncementsPanel() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    API.get("/admin/announcements").then((r) => setData(r.data)).catch(() => setData((p) => p || { announcements: [], staff_total: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);
  // While something is live, keep the acknowledgement counts current.
  const live = (data?.announcements || []).some((a) => a.live);
  useEffect(() => { if (!live) return undefined; const t = setInterval(load, 30000); return () => clearInterval(t); }, [live, load]);
  return (
    <div>
      <ConvoStyles />
      <Compose onPosted={load} />
      <List data={data} onReload={load} />
    </div>
  );
}
